import React, { Component } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import axios from'axios'

import {
  Alert,
  Button,
  ButtonContainer,
  Card,
  Col,
  FormInputRichText,
  List,
  ListItem,
  Row,
} from 'camino-react'

import { cloudhelpPatch } from 'Utilities/CloudHelp'

import RadioSelectType from './SurveyQuestionTypes/RadioSelectType'
import MultipleSelectType from './SurveyQuestionTypes/MultiSelectType'

class SurveyResponseForm extends Component {
  constructor(props) {
    super(props)

    const { survey_questions, survey_response } = props

    this.state = {
      submitBtnState: {
        text: 'Submit Survey',
        disabled: false
      },
      errors: [],
      unsubmittedResponses: [],
      survey_questions: survey_questions,
      survey_response: survey_response
    }
  }

  static getDerivedStateFromProps(props, state) {
    const textTypeQuestions = _.filter(props.survey_questions, { question_type: 'text' })

    // Check incoming props for text type questions. If present, set state to track
    // text input fields
    if (_.isEmpty(textTypeQuestions) || !_.isEmpty(state.unsubmittedResponses)) {
      return null
    } else {
      const initiatedTextAnswers = _.map(textTypeQuestions, textTypeQuestion => {
        return {
          questionId: textTypeQuestion.id,
          answer: '',
          questionType: 'text'
        }
      })
      return {
        unsubmittedResponses: initiatedTextAnswers
      }
    }
  }

  handleMultiSelect = (value, question, answer) => {
    this.setState(prevState => {
      const { unsubmittedResponses } = prevState

      const responseIndex = _.findIndex(unsubmittedResponses, { questionId: question.id })
      const oldUserResponse = responseIndex === -1 ? null : unsubmittedResponses[responseIndex]
      const oldSelectedAnswers = _.isNil(oldUserResponse) ? {} : oldUserResponse.answer

      const newSelectedAnswers = value ? Object.assign(oldSelectedAnswers, {[answer]: 'on'}) : _.omit(oldSelectedAnswers, [answer])

      const newUserResponse = {
        questionId: question.id,
        answer: newSelectedAnswers,
        questionType: question.question_type
      }

      responseIndex === -1 ? unsubmittedResponses.push(newUserResponse) : unsubmittedResponses[responseIndex] = newUserResponse

      return {
        unsubmittedResponses: unsubmittedResponses
      }
    })
  }

  handleRadioSelect = (value, answer, question) => {
    this.setState(prevState => {
      const { unsubmittedResponses } = prevState

      const newUserResponse = {
        questionId: question.id,
        answer: value ? answer : null,
        questionType: question.question_type
      }

      const responseIndex = _.findIndex(unsubmittedResponses, { questionId: question.id })
      responseIndex === -1 ? unsubmittedResponses.push(newUserResponse) : unsubmittedResponses[responseIndex] = newUserResponse

      return {
        unsubmittedResponses: unsubmittedResponses
      }
    })
  }

  handleTextInput = (value, questionId) => {
    this.setState(prevState => {
      const { unsubmittedResponses } = prevState
      const newUserResponse = {
        questionId: questionId,
        answer: value,
        questionType: 'text'
      }

      const responseIndex = _.findIndex(unsubmittedResponses, { questionId: questionId })
      responseIndex === -1 ? unsubmittedResponses.push(newUserResponse) : unsubmittedResponses[responseIndex] = newUserResponse

      return {
        unsubmittedResponses: unsubmittedResponses
      }
    })
  }

  submitSurvey = () => {
    const {
      unsubmittedResponses
    } = this.state

    const {
      user_token,
      survey_response,
      paths: { submitResponsePath }
    } = this.props

    const multiselectArray = _.filter(unsubmittedResponses, { questionType: 'multiselect' })
    const formattedMultiselectAnswers = _.forEach(multiselectArray, response => (
      _.zipObject(response.answer, _.map(response.answer, () => 'on'))
      ))

    const sortedAnswers = _.mapValues(_.keyBy(unsubmittedResponses, 'questionId'), 'answer')

    const fieldsToSubmit = Object.assign({}, survey_response, {
      answers: sortedAnswers
    })
    
    const data = {
      token: user_token,
      survey_response: fieldsToSubmit,
      answers: fieldsToSubmit.answers
    }

    this.setState({
      submitBtnState: {
        text: 'Submitting Survey...',
        disabled: true
      }
    })

    cloudhelpPatch(submitResponsePath, data)
      .then(() => {
        window.location.reload(true)
      })
      .catch((error) => {
        const { base: incomingErrors } = error.response.data.errors

        this.setState({
          errors: incomingErrors,
          submitBtnState: {
            text: 'Submit Survey',
            disabled: false
          }
        })
      })
  }

  render() {
    const {
      survey_questions,
      survey_response
    } = this.props

    const {
      unsubmittedResponses,
      submitBtnState,
      errors
    } = this.state

    const {
      handleMultiSelect,
      handleRadioSelect,
      handleTextInput,
      submitSurvey
    } = this

    return (
      <Row>
        <Col
          width={12}
        >
          <Card allowOverflow>
            {_.some(errors) && (
              <Alert
                type='error'
                title="Can't submit survey because:"
              >
                {errors}
              </Alert>
            )}
            {survey_response.answered && (
              <h2>Thank you for completing our survey!</h2>
            )}

            {!survey_response.answered && (
              <>
                <List>
                {_.map(survey_questions, question => (
                  <ListItem
                    key={question.question.id}
                    style={{flexDirection: 'column'}}
                  >
                    <h4 className="card__title">{question.question.question}</h4>

                    {question.question_type === 'range' && 
                      <RadioSelectType
                        handleRadioSelect={handleRadioSelect}
                        range={question.answers}
                        question={question.question}
                        unsubmittedResponses={unsubmittedResponses}
                      />
                    }

                    {question.question_type === 'text' &&
                      <FormInputRichText
                        value={_.find(unsubmittedResponses, {
                          questionId: question.question.id
                        }).answer}
                        onValue={value => handleTextInput(value, question.id)}
                      />
                    }

                    {question.question_type === 'multiselect' &&
                      <MultipleSelectType
                        question={question}
                        handleMultiSelect={handleMultiSelect}
                        unsubmittedResponses={unsubmittedResponses}
                      />
                    }

                    {question.question_type === 'singleselect' &&
                      <RadioSelectType
                        question={question}
                        handleRadioSelect={handleRadioSelect}
                        unsubmittedResponses={unsubmittedResponses}
                      />
                    }
                  </ListItem>
                ))}
                </List>
                <ButtonContainer center>
                  <Button
                    size='large'
                    onClick={submitSurvey}
                    disabled={submitBtnState.disabled}
                  >
                    {submitBtnState.text}
                  </Button>
                </ButtonContainer>
              </>
            )}
          </Card>
        </Col>
      </Row>
    )
  }
}

SurveyResponseForm.propTypes = {
  survey_questions: PropTypes.arrayOf(
    PropTypes.string
  ),
  survey_response: PropTypes.shape({}),
  paths: PropTypes.shape({
    submitResponsePath: PropTypes.string.isRequired
  }),
  currentUser: PropTypes.shape({}),
  user_token: PropTypes.string.isRequired
}

export default SurveyResponseForm
